import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Alert, Box, Button, CircularProgress, Grid, MenuItem, Paper, TextField, Typography } from '@mui/material';
import { AuthService } from '../../services/auth.service';
import { useToast } from '../../context/ToastContext';
import { ExportInvoiceService } from '../../services/exportInvoice.service';
import { BuyerOrderService } from '../../services/buyerOrder.service';
import { PackingListService } from '../../services/packingList.service';
import { BillOfLadingService } from '../../services/billOfLading.service';
import { ExportLcService } from '../../services/exportLc.service';
import { ICreateExportInvoiceRequest, INCOTERMS, Incoterm, IUpdateExportInvoiceRequest } from '../../interfaces/exportInvoice.interface';
import RefAutocomplete, { RefOption } from './common/RefAutocomplete';
import CommercialPartyAutocomplete from './CommercialPartyAutocomplete';
import { fetchAllPages } from './common/fetchAllPages';
import { formatDate, toLocalIsoDate } from '../../utils/dateUtils';

const today = () => toLocalIsoDate();

const EMPTY = {
    buyerOrderId: '', invoiceDate: today(), packingListId: '', billOfLadingId: '', exportLcId: '',
    incoterm: '' as Incoterm | '', incotermPlace: '', portOfLoading: '', portOfDischarge: '',
    finalDestination: '', countryOfOrigin: '', consigneeName: '', consigneeAddress: '',
    notifyPartyName: '', notifyPartyAddress: '', exporterBankDetails: '', remarks: '',
    lcNumber: '', lcDate: '', issuingBankName: '', issuingBankPartyId: '', currencyCode: '',
};

/** Export invoice header form (15.1, plan task 7). Create prefills from a buyer order
 * (`ICreateExportInvoiceRequest` -- buyer order picker is the FIRST control, no `invoiceNumber`
 * field, server-assigned) and navigates to the detail page on save. Edit
 * (`IUpdateExportInvoiceRequest`) carries no `buyerOrderId`/`invoiceNumber` -- both are immutable
 * after create -- but DOES require `incoterm`/`currencyCode` and MUST round-trip
 * `exporterBankDetails` on every save, since omitting it wipes the frozen bank-details block. */
const ExportInvoiceForm: React.FC = () => {
    const navigate = useNavigate();
    const toast = useToast();
    const { id } = useParams<{ id: string }>();
    const isEdit = !!id;
    const institutionId = AuthService.getSelectedInstitution()!;

    const [form, setForm] = useState({ ...EMPTY });
    const [buyerOrderOptions, setBuyerOrderOptions] = useState<RefOption[]>([]);
    const [packingListOptions, setPackingListOptions] = useState<RefOption[]>([]);
    const [billOfLadingOptions, setBillOfLadingOptions] = useState<RefOption[]>([]);
    const [exportLcOptions, setExportLcOptions] = useState<RefOption[]>([]);
    const [refOptionsLoading, setRefOptionsLoading] = useState(false);
    const [loading, setLoading] = useState(isEdit);
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState<string | null>(null);

    useEffect(() => {
        let cancelled = false;
        (async () => {
            setRefOptionsLoading(true);
            try {
                const [buyerOrders, packingLists, billsOfLading, exportLcs] = await Promise.all([
                    fetchAllPages((page, size) => BuyerOrderService.list(institutionId, page, size)),
                    fetchAllPages((page, size) => PackingListService.list(institutionId, page, size)),
                    fetchAllPages((page, size) => BillOfLadingService.list(institutionId, page, size)),
                    fetchAllPages((page, size) => ExportLcService.list(institutionId, { page, size })),
                ]);
                if (cancelled) return;
                setBuyerOrderOptions(buyerOrders.map(o => ({
                    id: o.id, label: o.orderDate ? `${o.orderNumber} — ${formatDate(o.orderDate)}` : o.orderNumber,
                })));
                setPackingListOptions(packingLists.map(p => ({ id: p.id, label: p.packingListNumber })));
                setBillOfLadingOptions(billsOfLading.map(b => ({ id: b.id, label: b.blNumber })));
                setExportLcOptions(exportLcs.map(l => ({ id: l.id, label: l.lcNumber })));
            } catch (e) {
                if (!cancelled) toast.showApiError(e, 'Failed to load buyer orders and linked documents');
            } finally {
                if (!cancelled) setRefOptionsLoading(false);
            }
        })();
        return () => { cancelled = true; };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [institutionId]);

    useEffect(() => {
        if (!id) return;
        (async () => {
            try {
                setLoading(true);
                const inv = await ExportInvoiceService.get(institutionId, id);
                setForm({
                    buyerOrderId: inv.buyerOrderId, invoiceDate: inv.invoiceDate,
                    packingListId: inv.packingListId ?? '', billOfLadingId: inv.billOfLadingId ?? '',
                    exportLcId: inv.exportLcId ?? '', incoterm: inv.incoterm ?? '',
                    incotermPlace: inv.incotermPlace ?? '', portOfLoading: inv.portOfLoading ?? '',
                    portOfDischarge: inv.portOfDischarge ?? '', finalDestination: inv.finalDestination ?? '',
                    countryOfOrigin: inv.countryOfOrigin ?? '', consigneeName: inv.consigneeName ?? '',
                    consigneeAddress: inv.consigneeAddress ?? '', notifyPartyName: inv.notifyPartyName ?? '',
                    notifyPartyAddress: inv.notifyPartyAddress ?? '', exporterBankDetails: inv.exporterBankDetails ?? '',
                    remarks: inv.remarks ?? '', lcNumber: inv.lcNumber ?? '', lcDate: inv.lcDate ?? '',
                    issuingBankName: inv.issuingBankName ?? '', issuingBankPartyId: inv.issuingBankPartyId ?? '',
                    currencyCode: inv.currencyCode ?? '',
                });
            } catch (e) {
                toast.showApiError(e, 'Failed to load export invoice');
            } finally {
                setLoading(false);
            }
        })();
    }, [id]); // eslint-disable-line react-hooks/exhaustive-deps

    const set = (k: keyof typeof EMPTY) => (e: React.ChangeEvent<HTMLInputElement>) =>
        setForm(f => ({ ...f, [k]: e.target.value }));

    // Picking a different LC re-copies its snapshot fields (LC number / date / issuing bank) into the
    // form, the same values create copies server-side. Clearing the LC leaves the fields alone.
    const handleLcChange = async (newId: string | null) => {
        setForm(f => ({ ...f, exportLcId: newId ?? '' }));
        if (!newId) return;
        try {
            const lc = await ExportLcService.getById(institutionId, newId);
            setForm(f => f.exportLcId !== newId ? f : ({
                ...f,
                lcNumber: lc.lcNumber ?? '',
                lcDate: lc.issueDate ?? '',
                issuingBankName: lc.issuingBank ?? '',
                issuingBankPartyId: lc.issuingBankPartyId ?? '',
            }));
        } catch (e) {
            toast.showApiError(e, 'Failed to load the selected LC details');
        }
    };

    const validate = (): string | null => {
        if (!isEdit && !form.buyerOrderId) return 'Buyer order is required.';
        if (!form.invoiceDate) return 'Invoice date is required.';
        if (isEdit && !form.incoterm) return 'Incoterm is required.';
        if (isEdit && !form.currencyCode.trim()) return 'Currency code is required.';
        return null;
    };

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        const validationError = validate();
        if (validationError) {
            setError(validationError);
            return;
        }
        setError(null);

        try {
            setSaving(true);
            if (isEdit) {
                const dto: IUpdateExportInvoiceRequest = {
                    invoiceDate: form.invoiceDate,
                    packingListId: form.packingListId.trim() || undefined,
                    billOfLadingId: form.billOfLadingId.trim() || undefined,
                    exportLcId: form.exportLcId.trim() || undefined,
                    exporterBankDetails: form.exporterBankDetails.trim() || undefined,
                    consigneeName: form.consigneeName.trim() || undefined,
                    consigneeAddress: form.consigneeAddress.trim() || undefined,
                    notifyPartyName: form.notifyPartyName.trim() || undefined,
                    notifyPartyAddress: form.notifyPartyAddress.trim() || undefined,
                    lcNumber: form.lcNumber.trim() || undefined,
                    lcDate: form.lcDate.trim() || undefined,
                    issuingBankName: form.issuingBankName.trim() || undefined,
                    issuingBankPartyId: form.issuingBankPartyId.trim() || undefined,
                    incoterm: form.incoterm as Incoterm,
                    incotermPlace: form.incotermPlace.trim() || undefined,
                    portOfLoading: form.portOfLoading.trim() || undefined,
                    portOfDischarge: form.portOfDischarge.trim() || undefined,
                    finalDestination: form.finalDestination.trim() || undefined,
                    countryOfOrigin: form.countryOfOrigin.trim() || undefined,
                    currencyCode: form.currencyCode.trim(),
                    remarks: form.remarks.trim() || undefined,
                };
                const saved = await ExportInvoiceService.update(institutionId, id!, dto);
                toast.showSuccess('Export invoice updated');
                navigate(`/commercial/export-invoices/${saved.id}`);
            } else {
                const dto: ICreateExportInvoiceRequest = {
                    buyerOrderId: form.buyerOrderId,
                    invoiceDate: form.invoiceDate,
                    packingListId: form.packingListId.trim() || undefined,
                    billOfLadingId: form.billOfLadingId.trim() || undefined,
                    exportLcId: form.exportLcId.trim() || undefined,
                    incoterm: form.incoterm ? (form.incoterm as Incoterm) : undefined,
                    incotermPlace: form.incotermPlace.trim() || undefined,
                    portOfLoading: form.portOfLoading.trim() || undefined,
                    portOfDischarge: form.portOfDischarge.trim() || undefined,
                    finalDestination: form.finalDestination.trim() || undefined,
                    countryOfOrigin: form.countryOfOrigin.trim() || undefined,
                    consigneeName: form.consigneeName.trim() || undefined,
                    consigneeAddress: form.consigneeAddress.trim() || undefined,
                    notifyPartyName: form.notifyPartyName.trim() || undefined,
                    notifyPartyAddress: form.notifyPartyAddress.trim() || undefined,
                    exporterBankDetails: form.exporterBankDetails.trim() || undefined,
                    remarks: form.remarks.trim() || undefined,
                };
                const saved = await ExportInvoiceService.create(institutionId, dto);
                toast.showSuccess('Export invoice created');
                navigate(`/commercial/export-invoices/${saved.id}`);
            }
        } catch (err) {
            toast.showApiError(err, isEdit ? 'Failed to update export invoice' : 'Failed to create export invoice');
        } finally {
            setSaving(false);
        }
    };

    if (loading) return <Box sx={{ p: 3 }}><CircularProgress /></Box>;

    return (
        <Box sx={{ p: 3, maxWidth: 960 }} data-help-page="commercial.invForm.page">
            <Typography variant="h5" sx={{ mb: 2 }}>{isEdit ? 'Edit Export Invoice' : 'New Export Invoice'}</Typography>
            {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
            <Paper sx={{ p: 3 }} data-help-section="commercial.invForm.section.details">
                <form onSubmit={handleSubmit}>
                    <Grid container spacing={2}>
                        {!isEdit && (
                            <Grid size={{ xs: 12, sm: 6 }}>
                                <RefAutocomplete
                                    data-help="commercial.invForm.field.buyerOrder"
                                    label="Buyer Order"
                                    options={buyerOrderOptions}
                                    loading={refOptionsLoading}
                                    value={form.buyerOrderId || null}
                                    onChange={(newId) => setForm(f => ({ ...f, buyerOrderId: newId ?? '' }))}
                                />
                            </Grid>
                        )}
                        <Grid size={{ xs: 12, sm: 6 }}>
                            <TextField data-help="commercial.invForm.field.invoiceDate" label="Invoice Date" type="date" fullWidth required InputLabelProps={{ shrink: true }}
                                value={form.invoiceDate} onChange={set('invoiceDate')} />
                        </Grid>

                        <Grid size={12}><Typography variant="subtitle2">Linked documents</Typography></Grid>
                        <Grid size={{ xs: 12, sm: 4 }}>
                            <RefAutocomplete
                                data-help="commercial.invForm.field.packingList"
                                label="Packing List"
                                options={packingListOptions}
                                loading={refOptionsLoading}
                                value={form.packingListId || null}
                                onChange={(newId) => setForm(f => ({ ...f, packingListId: newId ?? '' }))}
                            />
                        </Grid>
                        <Grid size={{ xs: 12, sm: 4 }}>
                            <RefAutocomplete
                                data-help="commercial.invForm.field.billOfLading"
                                label="Bill of Lading"
                                options={billOfLadingOptions}
                                loading={refOptionsLoading}
                                value={form.billOfLadingId || null}
                                onChange={(newId) => setForm(f => ({ ...f, billOfLadingId: newId ?? '' }))}
                            />
                        </Grid>
                        <Grid size={{ xs: 12, sm: 4 }}>
                            <RefAutocomplete
                                data-help="commercial.invForm.field.exportLc"
                                label="Export LC"
                                options={exportLcOptions}
                                loading={refOptionsLoading}
                                value={form.exportLcId || null}
                                onChange={handleLcChange}
                            />
                        </Grid>

                        <Grid size={12}><Typography variant="subtitle2">Incoterm & routing</Typography></Grid>
                        <Grid size={{ xs: 12, sm: 4 }}>
                            <TextField select data-help="commercial.invForm.field.incoterm" label="Incoterm" fullWidth required={isEdit} value={form.incoterm}
                                onChange={(e) => setForm(f => ({ ...f, incoterm: e.target.value as Incoterm }))}>
                                {!isEdit && <MenuItem value="">— use buyer order's —</MenuItem>}
                                {INCOTERMS.map(i => <MenuItem key={i.value} value={i.value}>{i.label}</MenuItem>)}
                            </TextField>
                        </Grid>
                        <Grid size={{ xs: 12, sm: 4 }}>
                            <TextField data-help="commercial.invForm.field.incotermPlace" label="Incoterm Place" fullWidth value={form.incotermPlace} onChange={set('incotermPlace')} />
                        </Grid>
                        <Grid size={{ xs: 12, sm: 4 }}>
                            <TextField data-help="commercial.invForm.field.countryOfOrigin" label="Country of Origin" fullWidth value={form.countryOfOrigin} onChange={set('countryOfOrigin')} />
                        </Grid>
                        <Grid size={{ xs: 12, sm: 4 }}>
                            <TextField data-help="commercial.invForm.field.portOfLoading" label="Port of Loading" fullWidth value={form.portOfLoading} onChange={set('portOfLoading')} />
                        </Grid>
                        <Grid size={{ xs: 12, sm: 4 }}>
                            <TextField data-help="commercial.invForm.field.portOfDischarge" label="Port of Discharge" fullWidth value={form.portOfDischarge} onChange={set('portOfDischarge')} />
                        </Grid>
                        <Grid size={{ xs: 12, sm: 4 }}>
                            <TextField data-help="commercial.invForm.field.finalDestination" label="Final Destination" fullWidth value={form.finalDestination} onChange={set('finalDestination')} />
                        </Grid>

                        {isEdit && (
                            <>
                                <Grid size={{ xs: 12, sm: 4 }}>
                                    <TextField data-help="commercial.invForm.field.currency" label="Currency Code" fullWidth required value={form.currencyCode}
                                        onChange={(e) => setForm(f => ({ ...f, currencyCode: e.target.value.toUpperCase() }))} />
                                </Grid>
                                <Grid size={12}><Typography variant="subtitle2">LC details</Typography></Grid>
                                <Grid size={{ xs: 12, sm: 4 }}>
                                    <TextField data-help="commercial.invForm.field.lcNumber" label="LC Number" fullWidth value={form.lcNumber} onChange={set('lcNumber')} />
                                </Grid>
                                <Grid size={{ xs: 12, sm: 4 }}>
                                    <TextField data-help="commercial.invForm.field.lcDate" label="LC Date" type="date" fullWidth InputLabelProps={{ shrink: true }}
                                        value={form.lcDate} onChange={set('lcDate')} />
                                </Grid>
                                <Grid size={{ xs: 12, sm: 4 }}>
                                    <TextField data-help="commercial.invForm.field.issuingBankName" label="Issuing Bank Name" fullWidth value={form.issuingBankName} onChange={set('issuingBankName')} />
                                </Grid>
                                <Grid size={{ xs: 12, sm: 6 }}>
                                    <CommercialPartyAutocomplete
                                        institutionId={institutionId}
                                        partyTypes={['BANK']}
                                        data-help="commercial.invForm.field.issuingBankParty"
                                        label="Issuing Bank (party)"
                                        value={form.issuingBankPartyId || null}
                                        onChange={(party) => setForm(f => ({ ...f, issuingBankPartyId: party?.id ?? '' }))}
                                    />
                                </Grid>
                            </>
                        )}

                        <Grid size={12}><Typography variant="subtitle2">Consignee & notify party</Typography></Grid>
                        <Grid size={{ xs: 12, sm: 6 }}>
                            <TextField data-help="commercial.invForm.field.consigneeName" label="Consignee Name" fullWidth value={form.consigneeName} onChange={set('consigneeName')} />
                        </Grid>
                        <Grid size={{ xs: 12, sm: 6 }}>
                            <TextField data-help="commercial.invForm.field.consigneeAddress" label="Consignee Address" fullWidth value={form.consigneeAddress} onChange={set('consigneeAddress')} />
                        </Grid>
                        <Grid size={{ xs: 12, sm: 6 }}>
                            <TextField data-help="commercial.invForm.field.notifyPartyName" label="Notify Party Name" fullWidth value={form.notifyPartyName} onChange={set('notifyPartyName')} />
                        </Grid>
                        <Grid size={{ xs: 12, sm: 6 }}>
                            <TextField data-help="commercial.invForm.field.notifyPartyAddress" label="Notify Party Address" fullWidth value={form.notifyPartyAddress} onChange={set('notifyPartyAddress')} />
                        </Grid>

                        <Grid size={12}>
                            <TextField data-help="commercial.invForm.field.exporterBankDetails" label="Exporter Bank Details" fullWidth multiline rows={4}
                                helperText="Printed on the invoice. Clearing this field removes the bank details from the invoice."
                                value={form.exporterBankDetails} onChange={set('exporterBankDetails')} />
                        </Grid>
                        <Grid size={12}>
                            <TextField data-help="commercial.invForm.field.remarks" label="Remarks" fullWidth multiline rows={3} value={form.remarks} onChange={set('remarks')} />
                        </Grid>

                        <Grid size={12} sx={{ display: 'flex', gap: 1 }}>
                            <Button data-help="commercial.invForm.save" type="submit" variant="contained" disabled={saving}>
                                {saving ? 'Saving…' : isEdit ? 'Save Changes' : 'Create Export Invoice'}
                            </Button>
                            <Button data-help="commercial.invForm.cancel" variant="outlined" onClick={() => navigate(-1)}>Cancel</Button>
                        </Grid>
                    </Grid>
                </form>
            </Paper>
        </Box>
    );
};

export default ExportInvoiceForm;
